import React, { useMemo } from 'react';
import { useHistory } from 'react-router';
import { transparentize } from 'polished';
import _get from 'lodash/get';
import _keyBy from 'lodash/keyBy';
import { makeStyles } from '@material-ui/core';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import getHostName from 'helpers/getHostName';
import * as Types from 'pages/RolesAndPermissions/typings';
import * as Sift from '@sift/types';
import { Theme } from 'styles/ThemeTyping';
import { HOME_URL, ROLES_AND_PERMISSIONS_URL } from 'components/Router/consts';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Table from 'components/Table';
import TableHead from 'components/Table/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCellLink from 'components/Table/TableCellLink';
import TableHeadCell from 'components/Table/TableHeadCell';
import TableSearchTextField from 'components/Table/TableSearchTextField';
import TablePagination from 'components/Table/TablePagination';
import IconButton from '@material-ui/core/IconButton';
import PersonSearchIcon from 'components/Icons/PersonSearchIcon';
import Chip from 'pages/RolesAndPermissions/molecules/Chip';
import RoleOverflowChip from './RoleOverflowChip';

interface Props {
  usersData: Sift.Person[];
  pagination: any;
}

function UsersTab({ usersData, pagination }: Props) {
  const clientSubdomain = useSelector((state: any) => state.client.subdomain);
  const hostName = getHostName();

  const tableColumns = useMemo(
    () => [
      { field: 'name', headerName: 'Name', width: 200 },
      { field: 'roles', headerName: 'Roles', width: 600 },
      { field: 'viewAs', headerName: 'View As', width: 100 },
    ],
    [],
  );

  const classes = useStyles({ tableColumns });
  const history = useHistory();

  const query = useMemo(() => queryString.parse(history.location.search), [
    history.location.search,
  ]);

  const getColumnWidth = (fieldName: string) => {
    const column = tableColumns.find(col => col.field === fieldName);
    return column ? column.width : 'auto';
  };

  return (
    <Paper className={classes.root}>
      <div className={classes.tableHeader}>
        <Typography variant="h5" className={classes.title}>
          Users
        </Typography>
        <TableSearchTextField hintText="Search Users" query={query} />
      </div>

      <div className={classes.table}>
        <Table>
          <TableHead>
            {tableColumns.map(column => (
              <TableHeadCell
                key={column.field}
                className={classes.tableHeaderCell}
                // (TODO - Dylan) update this to be used in the styling class
                style={{ width: getColumnWidth(column.field) }}
              >
                {column.headerName}
              </TableHeadCell>
            ))}
          </TableHead>
          <TableBody>
            {usersData.map((user, index) => (
              <TableRow key={index} className={classes.row} onClick={() => {}}>
                <TableCellLink
                  className={classes.cell}
                  to={`${HOME_URL}${ROLES_AND_PERMISSIONS_URL}/users/${user.id}`}
                >
                  {user.displayName}
                </TableCellLink>
                <TableCellLink
                  to={`${HOME_URL}${ROLES_AND_PERMISSIONS_URL}/users/${user.id}`}
                  className={classes.cell}
                >
                  <div className={classes.roleContainer}>
                    {user._roles.slice(0, 4).map((role: Types.Role) => (
                      <Chip key={role.name} label={role.name} />
                    ))}
                    {user._roles.length > 4 && (
                      <RoleOverflowChip
                        className={classes.roleChip}
                        roleOverflow={user._roles.length - 4}
                      />
                    )}
                  </div>
                </TableCellLink>
                <TableCellLink className={classes.cell}>
                  <IconButton
                    size="small"
                    href={`https://${clientSubdomain}.${hostName}.com?proxyAs=${user.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-cy="view-as-button"
                    aria-label={`View Sift as ${user.displayName}`}
                  >
                    <PersonSearchIcon className={classes.personSearchIcon} />
                  </IconButton>
                </TableCellLink>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination component="div" pagination={pagination} />
      </div>
    </Paper>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    padding: 24,
  },
  title: {
    marginBottom: theme.spacing(2.5),
  },
  table: {
    marginTop: theme.spacing(3),
    maxWidth: 1400,
  },
  tableHeader: {},
  tableHeaderCell: {
    fontWeight: theme.typography.fontWeightSemiBold,
  },
  row: {
    height: 53,
  },
  cell: {
    padding: 0,
  },
  infoIcon: {
    maxHeight: 18,
    maxWidth: 18,
    marginLeft: theme.spacing(),
  },
  roleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  roleChip: {
    borderRadius: 4,
    backgroundColor: transparentize(0.96, theme.palette.secondary.main),
    color: theme.palette.secondary.main,
    width: 'fit-content',
    maxHeight: 20,
    paddingLeft: theme.spacing(),
    paddingRight: theme.spacing(),
    marginRight: theme.spacing(0.5),
  },
  personSearchIcon: {
    maxHeight: 16,
    maxWidth: 16,
  },
}));

export default UsersTab;
